import React from "react";
import { Box, Typography, Button, Breadcrumbs, Link, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useTranslation } from "react-i18next";
import { useLayoutShell } from "./layoutShellContext.js";
import { useHelpStore } from "../store/helpStore.js";

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: { label: string; href?: string }[];
  actionLabel?: string;
  onAction?: () => void;
  actionIcon?: React.ReactNode;
  actions?: React.ReactNode;
  /** Se valorizzato, mostra un tasto "Aggiorna" che ricarica i dati della pagina. */
  onRefresh?: () => void;
}

export function PageHeader({
  title, subtitle, breadcrumbs, actionLabel, onAction, actionIcon, actions, onRefresh
}: PageHeaderProps) {
  const { t } = useTranslation();
  const layoutShell = useLayoutShell();
  const openHelp = useHelpStore((s) => s.setOpen);

  return (
    <Box data-tour="page-header" sx={{
      px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2,
      borderBottom: "1px solid rgba(0,0,0,.06)",
      bgcolor: "background.paper",
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      alignItems: { xs: "stretch", sm: "flex-start" },
      justifyContent: "space-between",
      gap: { xs: 1.5, sm: 2 }
    }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0, flex: 1 }}>
        {layoutShell && (
          <IconButton
            size="medium"
            aria-label={layoutShell.isMobile ? t("common.openNavMenu") : t("common.toggleNavMenu")}
            onClick={() => {
              if (layoutShell.isMobile) layoutShell.setMobileNavOpen(true);
              else layoutShell.toggleDesktopNav();
            }}
            data-tour="nav-toggle"
            sx={{ mt: 0.25, flexShrink: 0, color: "text.primary" }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box data-tour="page-title" sx={{ minWidth: 0 }}>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <Breadcrumbs sx={{ mb: 0.5 }}>
            {breadcrumbs.map((b, i) =>
              b.href
                ? <Link key={i} href={b.href} underline="hover" color="text.secondary" fontSize="0.78rem">{b.label}</Link>
                : <Typography key={i} color="text.secondary" fontSize="0.78rem">{b.label}</Typography>
            )}
          </Breadcrumbs>
        )}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
          <Tooltip title={t("help.button")}>
            <IconButton
              data-tour="help"
              size="small"
              onClick={() => openHelp(true)}
              aria-label={t("help.button")}
              sx={{ color: "text.secondary", ml: -0.5, mr: 0.25 }}
            >
              <HelpOutlineIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
        </Box>
      </Box>
      <Box sx={{
        display: "flex", gap: 1, mt: { xs: 0, sm: 0.5 }, flexShrink: 0,
        alignItems: "center",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        justifyContent: { xs: "flex-end", sm: "flex-start" }
      }}>
        {onRefresh && (
          <Tooltip title={t("common.refresh")}>
            <IconButton data-tour="page-refresh" size="medium" onClick={onRefresh} aria-label={t("common.refresh")} sx={{ color: "text.secondary" }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        )}
        {actions}
        {actionLabel && onAction && (
          <Button
            data-tour="page-action"
            variant="contained"
            startIcon={actionIcon ?? <AddIcon />}
            onClick={onAction}
            size="medium"
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}
